import {
  createListenerMiddleware,
  TypedStartListening,
  TypedAddListener,
  addListener as addListenerRedux,
  UnknownAction,
  ListenerEffectAPI,
  UnsubscribeListener,
} from '@reduxjs/toolkit';

import { State, AppDispatch } from './state.types';

export const listenerMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<State, AppDispatch>;

export const startListening =
  listenerMiddleware.startListening as AppStartListening;

export const addListener = addListenerRedux as TypedAddListener<
  State,
  AppDispatch
>;

// While in development when the app is reloading dynamically the listeners must be cleared
// Otherwise they get attached multiple times for each reload...
listenerMiddleware.clearListeners();

type ThrottledListenerOptions = {
  actionCreator?: unknown;
  type?: string;
  matcher?: (action: UnknownAction) => boolean;
  predicate?: (action: UnknownAction, currentState: State) => boolean;
  effect: (
    action: UnknownAction,
    api: ListenerEffectAPI<State, AppDispatch>,
  ) => void | Promise<void>;
};

export function startThrottledListener(
  options: ThrottledListenerOptions,
  timeoutMs = 1000,
): UnsubscribeListener {
  let lastExecutionTime = 0;

  const throttledEffect = async (
    action: UnknownAction,
    api: ListenerEffectAPI<State, AppDispatch>,
  ): Promise<void> => {
    const now = Date.now();

    if (now - lastExecutionTime < timeoutMs) {
      return;
    }

    lastExecutionTime = now;
    return options.effect(action, api);
  };

  return startListening({
    ...options,
    effect: throttledEffect,
  } as Parameters<AppStartListening>[0]);
}
